import { ParamMap, Params } from '@angular/router';

import { DemandStatus } from '../../../../Enums/demand-status.enum';
import { DEMANDS_SEARCH_MAX_LENGTH } from '../../../Catalog/Consts/search-max-length.const';
import {
  ParsedQueryParam,
  parseDateRangeParams,
  parseEnumParam,
  parseNullableEnumParam,
  parsePageParam,
  parseSearchParam,
} from '../../../Catalog/Shared/query-param-parse.util';
import {
  ALL_DEMANDS_DEFAULT_ORDER,
  ALL_DEMANDS_DEFAULT_ORDER_BY,
  ALL_DEMANDS_DEFAULT_PAGE,
  ALL_DEMANDS_DEFAULT_PAGE_SIZE,
  ALL_DEMANDS_DEVICE_MAX_LENGTH,
  ALL_DEMANDS_ORDER_BY_VALUES,
  ALL_DEMANDS_ORDER_VALUES,
  ALL_DEMANDS_PLATFORM_VALUES,
  ALL_DEMANDS_STATUS_VALUES,
} from '../Consts/list-all-demands.const';
import {
  AllDemandsFilterCriteria,
  AllDemandsFilters,
} from '../Interfaces/all-demands-filters.interface';
import { ParsedAllDemandsQuery } from '../Interfaces/parsed-all-demands-query.interface';
import { ReportSubjectTab } from '../Types/report-subject-tab.type';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseUuidParam(raw: string | null): ParsedQueryParam<string | null> {
  if (raw === null) return { value: null, malformed: false };
  return UUID_PATTERN.test(raw)
    ? { value: raw, malformed: false }
    : { value: null, malformed: true };
}

function parseStatusParams(raw: string[]): ParsedQueryParam<DemandStatus[]> {
  const known = raw.filter((value): value is DemandStatus =>
    ALL_DEMANDS_STATUS_VALUES.includes(value as DemandStatus),
  );
  const unique = [...new Set(known)];
  return { value: unique, malformed: unique.length !== raw.length };
}

type ChoiceCriteria = Pick<AllDemandsFilterCriteria, 'statuses' | 'platform'>;
type TextCriteria = Pick<AllDemandsFilterCriteria, 'search' | 'device'>;
type DateCriteria = Pick<
  AllDemandsFilterCriteria,
  'requestedFrom' | 'requestedTo' | 'updatedFrom' | 'updatedTo'
>;
type PersonCriteria = Pick<
  AllDemandsFilterCriteria,
  'requesterId' | 'subjectId' | 'approverId' | 'moderatorId' | 'decidedById'
>;

function parseChoiceCriteria(params: ParamMap): ParsedQueryParam<ChoiceCriteria> {
  const statuses = parseStatusParams(params.getAll('status'));
  const platform = parseNullableEnumParam(params.get('platform'), ALL_DEMANDS_PLATFORM_VALUES);
  return {
    value: { statuses: statuses.value, platform: platform.value },
    malformed: statuses.malformed || platform.malformed,
  };
}

function parseTextCriteria(params: ParamMap): ParsedQueryParam<TextCriteria> {
  const search = parseSearchParam(params.get('search'), DEMANDS_SEARCH_MAX_LENGTH);
  const device = parseSearchParam(params.get('device'), ALL_DEMANDS_DEVICE_MAX_LENGTH);
  return {
    value: { search: search.value, device: device.value },
    malformed: search.malformed || device.malformed,
  };
}

function parseDateCriteria(params: ParamMap): ParsedQueryParam<DateCriteria> {
  const requested = parseDateRangeParams(params.get('requestedFrom'), params.get('requestedTo'));
  const updated = parseDateRangeParams(params.get('updatedFrom'), params.get('updatedTo'));
  return {
    value: {
      requestedFrom: requested.value.from,
      requestedTo: requested.value.to,
      updatedFrom: updated.value.from,
      updatedTo: updated.value.to,
    },
    malformed: requested.malformed || updated.malformed,
  };
}

function parsePersonCriteria(params: ParamMap): ParsedQueryParam<PersonCriteria> {
  const requester = parseUuidParam(params.get('requesterId'));
  const subject = parseUuidParam(params.get('subjectId'));
  const approver = parseUuidParam(params.get('approverId'));
  const moderator = parseUuidParam(params.get('moderatorId'));
  const decidedBy = parseUuidParam(params.get('decidedById'));
  return {
    value: {
      requesterId: requester.value,
      subjectId: subject.value,
      approverId: approver.value,
      moderatorId: moderator.value,
      decidedById: decidedBy.value,
    },
    malformed: [requester, subject, approver, moderator, decidedBy].some((part) => part.malformed),
  };
}

function parseCriteria(params: ParamMap): ParsedQueryParam<AllDemandsFilterCriteria> {
  const choices = parseChoiceCriteria(params);
  const texts = parseTextCriteria(params);
  const dates = parseDateCriteria(params);
  const people = parsePersonCriteria(params);
  return {
    value: { ...choices.value, ...texts.value, ...dates.value, ...people.value },
    malformed: [choices, texts, dates, people].some((part) => part.malformed),
  };
}

function parsePaging(
  params: ParamMap,
): ParsedQueryParam<Pick<AllDemandsFilters, 'page' | 'pageSize' | 'orderBy' | 'order'>> {
  const page = parsePageParam(params.get('page'), ALL_DEMANDS_DEFAULT_PAGE);
  const orderBy = parseEnumParam(
    params.get('orderBy'),
    ALL_DEMANDS_ORDER_BY_VALUES,
    ALL_DEMANDS_DEFAULT_ORDER_BY,
  );
  const order = parseEnumParam(
    params.get('order'),
    ALL_DEMANDS_ORDER_VALUES,
    ALL_DEMANDS_DEFAULT_ORDER,
  );

  return {
    value: {
      page: page.value,
      pageSize: ALL_DEMANDS_DEFAULT_PAGE_SIZE,
      orderBy: orderBy.value,
      order: order.value,
    },
    malformed: page.malformed || orderBy.malformed || order.malformed,
  };
}

export function parseAllDemandsQueryParams(
  params: ParamMap,
  permittedTabs: ReadonlyArray<ReportSubjectTab>,
): ParsedAllDemandsQuery {
  const tab = parseEnumParam(
    params.get('subjectTab'),
    permittedTabs,
    permittedTabs[0] ?? 'application',
  );
  const criteria = parseCriteria(params);
  const paging = parsePaging(params);

  return {
    filters: { ...criteria.value, ...paging.value },
    tab: tab.value,
    malformed: tab.malformed || criteria.malformed || paging.malformed,
  };
}

export function buildAllDemandsQueryParams(
  filters: AllDemandsFilters,
  tab: ReportSubjectTab,
  defaultTab: ReportSubjectTab,
): Params {
  return {
    subjectTab: tab === defaultTab ? null : tab,
    status: filters.statuses.length > 0 ? filters.statuses : null,
    search: filters.search.length > 0 ? filters.search : null,
    requestedFrom: filters.requestedFrom,
    requestedTo: filters.requestedTo,
    updatedFrom: filters.updatedFrom,
    updatedTo: filters.updatedTo,
    platform: filters.platform,
    device: filters.device.length > 0 ? filters.device : null,
    requesterId: filters.requesterId,
    subjectId: filters.subjectId,
    approverId: filters.approverId,
    moderatorId: filters.moderatorId,
    decidedById: filters.decidedById,
    page: filters.page === ALL_DEMANDS_DEFAULT_PAGE ? null : filters.page,
    orderBy: filters.orderBy === ALL_DEMANDS_DEFAULT_ORDER_BY ? null : filters.orderBy,
    order: filters.order === ALL_DEMANDS_DEFAULT_ORDER ? null : filters.order,
  };
}

export function areAllDemandsFiltersEqual(a: AllDemandsFilters, b: AllDemandsFilters): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
